import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Anchor, Mail, Lock, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatError } from "../lib/api";

export default function Login() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const next = search.get("next") || "/mon-espace";
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bienvenue à bord !");
      nav(next);
    } catch (e2) {
      toast.error(formatError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] bg-blanc grid place-items-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <span className="w-10 h-10 rounded-full bg-ocean text-blanc grid place-items-center">
            <Anchor className="w-4 h-4" />
          </span>
          <span className="font-display text-xl font-bold text-ocean">Le Bon Matelot</span>
        </Link>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-ocean tracking-tight">
          Heureux de vous revoir
        </h1>
        <p className="mt-2 text-ocean/70">Connectez-vous pour embarquer.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</label>
            <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-brume/30 border-brume rounded-2xl py-6" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Mot de passe</label>
            <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-brume/30 border-brume rounded-2xl py-6" />
          </div>
          <Button data-testid="login-submit" disabled={busy} className="w-full bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-full py-6 tactile shadow-matelot">
            <LogIn className="w-4 h-4 mr-2" /> {busy ? "Connexion…" : "Se connecter"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-bretagne/70">
          <div className="flex-1 h-px bg-brume" />
          ou
          <div className="flex-1 h-px bg-brume" />
        </div>

        <button
          data-testid="login-google"
          onClick={googleLogin}
          className="w-full inline-flex items-center justify-center gap-3 bg-blanc border-2 border-brume text-ocean font-bold rounded-full py-3.5 hover:bg-brume/40 tactile"
        >
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.7v2.3h3a8.8 8.8 0 0 0 2.6-6.8z"/><path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.3a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z"/><path fill="#FBBC05" d="M4 10.6a5.4 5.4 0 0 1 0-3.4V4.9H1a9 9 0 0 0 0 8.1l3-2.4z"/><path fill="#EA4335" d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.3A5.4 5.4 0 0 1 9 3.6z"/></svg>
          Continuer avec Google
        </button>

        <p className="mt-8 text-center text-sm text-ocean/70">
          Pas encore de compte ?{" "}
          <Link to="/inscription" data-testid="goto-register" className="text-corail font-bold hover:underline">
            Rejoindre la communauté
          </Link>
        </p>
      </div>
    </div>
  );
}
